import {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { debounce } from 'lodash';

import moment from 'moment';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';

// Redux
import {
  setSaveStatus,
  updateMentoringProgram,
} from 'redux/actions/mentoring-programs';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';

// Components
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import NvPopover from 'shared/components/nv-popover';
import NvTooltip from 'shared/components/nv-tooltip';
import NumberInput from 'athena/components/number-input';
import NvSwitch from 'shared/components/inputs/nv-switch';

import MentoringProgramContext from '../../context';
import { overlayStyles } from '../program-setup/styles';

const DEFAULT_PROGRAM_REFLECTIONS_SETTINGS = {
  enabled: false,
  sessionToStart: 12,
  minSession: 1,
};

const ProgramReflections = () => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);

  const {
    programReflectionEnabled = DEFAULT_PROGRAM_REFLECTIONS_SETTINGS.enabled,
    programReflectionSession = DEFAULT_PROGRAM_REFLECTIONS_SETTINGS.sessionToStart,
    releasedDate,
  } = mentoringProgram || {};

  const [enabled, setEnabled] = useState(false);
  const [sessionToStart, setSessionToStart] = useState(programReflectionSession);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const translationBase = t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_WRAP_UP.PROGRAM_REFLECTIONS;
  const keyForAriaLabel = enabled
    ? 'TURN_PROGRAM_REFLECTIONS_OFF'
    : 'TURN_PROGRAM_REFLECTIONS_ON';
  const label = translationBase.ARIA_LABEL[keyForAriaLabel]();

  const updateProgram = useCallback(
    (nextEnabled: boolean, nextSession: number) => {
      if (!mentoringProgram?.id) {
        return;
      }
      dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
      wrapThunkAction(
        dispatch(
          updateMentoringProgram({
            id: mentoringProgram.id,
            programReflectionSession: nextSession,
            programReflectionEnabled: nextEnabled,
          }),
        ),
      )
        .then(() => {
          dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
        })
        .finally(() => {
          setTimeout(() => {
            dispatch(setSaveStatus({ newStatus: null }));
          }, 3000);
          setShowConfirmation(false);
        });
    },
    [dispatch, mentoringProgram?.id],
  );

  const debouncedUpdate = useMemo(
    () => debounce(updateProgram, 500),
    [updateProgram],
  );

  const handleChange = () => {
    if (enabled && moment().isAfter(releasedDate)) {
      setShowConfirmation(true);
    } else {
      const nextEnabled = !enabled;
      setEnabled(nextEnabled);
      updateProgram(nextEnabled, sessionToStart);
    }
  };

  useEffect(() => {
    setEnabled(programReflectionEnabled);
    setSessionToStart(programReflectionSession);
  }, [programReflectionEnabled, programReflectionSession]);

  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

  return (
    <div className='mb-2'>
      <div className='d-flex mb-2 align-items-center'>
        <div className='heading-5 mr-2'>
          {translationBase.TITLE()}
        </div>
        <NvPopover
          enabled
          show={showConfirmation}
          placement='top'
          overlayStyles={overlayStyles}
          content={(
            <NvConfirmationPopover
              onCancel={() => setShowConfirmation(false)}
              onConfirm={() => {
                setEnabled(false);
                updateProgram(false, sessionToStart);
              }}
              header={translationBase.WARNING.TITLE()}
              message={translationBase.WARNING.CONTENT()}
              cancelBtnText={t.FORM.CANCEL()}
              confirmBtnText={t.FORM.OK()}
            />
          )}
          rootClose
          onHide={() => setShowConfirmation(false)}
          className='d-inline-flex mw-100'
          preventOverflow
        >
          <NvTooltip
            preventOverflow={false}
            text={label}
          >
            <NvSwitch
              checked={enabled}
              onChange={handleChange}
              enabledLabel={t.SHARED.ON().toUpperCase()}
              disabledLabel={t.SHARED.OFF().toUpperCase()}
              dataQa={config.pendo.athena.mentorshipProgram.settings.programWrapUp.enabledSwitch}
              aria-label={label}
            />
          </NvTooltip>
        </NvPopover>
      </div>
      <div className='description text-regular mb-2'>
        {translationBase.DESCRIPTION()}
      </div>
      <div className='d-flex align-items-center'>
        {translationBase.STARTING_SESSION()}
        <NumberInput
          className='ml-1'
          name='expression'
          disabled={!enabled}
          min={1}
          data-qa={config.pendo.athena.mentorshipProgram.settings.programWrapUp.sessionNumberInputField}
          value={sessionToStart}
          tooltipTextOnDisabledUp={t.MENTORING_PROGRAMS.TIME_ZONE_OFFSET_INPUT.TOOLTIP_TEXT_ON_DISABLED_UP()}
          onChange={(sessionNumber) => {
            if (enabled) {
              setSessionToStart(sessionNumber);
              debouncedUpdate(enabled, sessionNumber);
            }
          }}
        />
      </div>
    </div>
  );
};

export default ProgramReflections;
